/* UI primitives — exact port of code.txt's "UI PRIMITIVES — Botpresso SIMPLE LIGHT"
   section (plus the export helpers and brand SVGs that live just above them there).
   React props become plain arguments; onClick handlers become `attrs` (the caller wires
   them by attribute). NOTE: unlike React, raw CSS needs explicit px units everywhere. */

import Papa from "papaparse";
import * as XLSX from "xlsx";

export const FONT = "'Figtree', ui-sans-serif, system-ui, -apple-system, sans-serif";
export const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

export const T = {
  bg: "#F7F7F9", surface: "#FFFFFF",
  ink: "#0A0A0B", sub: "#3F3F46", muted: "#57575F",
  line: "#DDDDE3", lineSoft: "#EAEAEF",
  violet: "#5509F8", violetSoft: "rgba(85,9,248,0.07)", violetLine: "rgba(85,9,248,0.18)",
  magenta: "#F50FAF", magentaSoft: "rgba(245,15,175,0.07)",
  green: "#16A34A", greenSoft: "#E7F6EC",
  amber: "#D97706", amberSoft: "#FBF1E3",
  red: "#DC2626", redSoft: "#FBE9E9",
  blue: "#2563EB", blueSoft: "#E8F0FE",
  dark: "#16161A",
};

export const fmt = n => { if (n == null || isNaN(n)) return "—"; if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return String(Math.round(n)); };

/* ---------- tiers & label colors (UI halves of the engine labels) ---------- */
import { searchLabel, aiLabel } from "@agf/analyzer";
export { searchLabel, aiLabel };
export const TIER_STYLE = {
  "VERY HIGH": { c: T.red, bg: T.redSoft },
  "HIGH": { c: T.amber, bg: T.amberSoft },
  "MEDIUM": { c: T.blue, bg: T.blueSoft },
  "LOW": { c: T.muted, bg: T.lineSoft },
};
export const SEARCH_LABEL_C = { Strong: T.green, Moderate: T.blue, Weak: T.amber };
export const SEARCH_LABEL_BG = { Strong: T.greenSoft, Moderate: T.blueSoft, Weak: T.amberSoft };
export const CONF_TEXT = {
  HIGH: "This page ranks well for many searches related to this topic, so we're confident this topic drives a meaningful part of its search visibility.",
  MEDIUM: "This page ranks for several searches related to this topic. The topic link is likely but based on a smaller set of searches.",
  LOW: "Either only a few searches back this topic, or we couldn't match it to one of your pages by URL \u2014 treat this estimate as directional.",
};

/* ---------- EXPORT (CSV / XLSX / JSON on every table — Botpresso convention).
   CSV gets a BOM so Excel opens it as UTF-8 (plan §16 fix). ---------- */
function download(name, blob) { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); }
export function exportRows(rows, base, format) {
  if (!rows.length) return;
  if (format === "JSON") return download(base + ".json", new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" }));
  if (format === "CSV") { const csv = Papa.unparse(rows); return download(base + ".csv", new Blob(["\uFEFF" + csv], { type: "text/csv" })); }
  const ws = XLSX.utils.json_to_sheet(rows); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "data");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" }); download(base + ".xlsx", new Blob([out], { type: "application/octet-stream" }));
}

/* ExportBar registers its rows under a token; ONE delegated document-level click
   handler serves every bar ever rendered. Self-audit 2026-08-26: the old design
   required each view to call wireExports(root) after rendering — and NO view ever
   did, so every export button in the app was silently dead. Delegation removes the
   wiring obligation entirely (the same bug class that killed the Pages search box). */
const exportRegistry = new Map();
let exportSeq = 0;
const EXPORT_REGISTRY_MAX = 64; // oldest tokens die first; only bars still in the DOM matter
export function ExportBar(rows, base) {
  const token = "exp" + (++exportSeq);
  exportRegistry.set(token, { rows, base });
  if (exportRegistry.size > EXPORT_REGISTRY_MAX) exportRegistry.delete(exportRegistry.keys().next().value);
  return `<div data-export-bar="${token}" style="display:inline-flex;gap:4px">
    ${["CSV", "XLSX", "JSON"].map(f => `
      <button title="Export ${f}" style="display:inline-flex;align-items:center;gap:4px;background:${T.surface};color:${T.sub};border:1px solid ${T.line};border-radius:7px;font-family:${MONO};font-weight:600;padding:5px 9px;font-size:10.5px;cursor:pointer;letter-spacing:0.2px">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>${f}
      </button>`).join("")}
  </div>`;
}
if (typeof document !== "undefined") {
  document.addEventListener("click", e => {
    const btn = e.target.closest && e.target.closest("[data-export-bar] button");
    if (!btn) return;
    const reg = exportRegistry.get(btn.closest("[data-export-bar]").dataset.exportBar);
    if (reg) exportRows(reg.rows, reg.base, btn.textContent.trim());
  });
}

/* ---------- brand marks (inline SVG) ---------- */
let sparkSeq = 0;
export function AioSparkle(size = 13, style = "") {
  const id = "aioGrad" + (++sparkSeq);
  return `<svg width="${size}" height="${size}" viewBox="0 0 100 100" style="flex-shrink:0;vertical-align:-2px;${style}">
    <defs><linearGradient id="${id}" x1="0" y1="1" x2="1" y2="0"><stop offset="38%" stop-color="#2E7CF6"/><stop offset="100%" stop-color="#A97FF2"/></linearGradient></defs>
    <path d="M50 0 C53.5 27.5 72.5 46.5 100 50 C72.5 53.5 53.5 72.5 50 100 C46.5 72.5 27.5 53.5 0 50 C27.5 46.5 46.5 27.5 50 0 Z" fill="url(#${id})"/>
  </svg>`;
}
export function BrandMark(size = 28) {
  return `<svg width="${size}" height="${size}" viewBox="0 0 100 100" fill="none">
    <line x1="45" y1="46" x2="34" y2="26" stroke="#5509F8" stroke-width="7" stroke-linecap="round"/>
    <circle cx="31" cy="20" r="11" fill="#5509F8"/>
    <line x1="55" y1="46" x2="64" y2="30" stroke="#5509F8" stroke-width="7" stroke-linecap="round"/>
    <circle cx="67" cy="25" r="9" fill="#5509F8"/>
    <rect x="6" y="42" width="88" height="52" rx="26" fill="#5509F8"/>
    <path d="M26 72 L35 63 L44 72" stroke="#fff" stroke-width="7" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M56 72 L65 63 L74 72" stroke="#fff" stroke-width="7" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}
export function AppLogo(size = 30) {
  return `<span style="position:relative;display:inline-flex;width:${size * 1.14}px;height:${size}px;flex-shrink:0">
    ${BrandMark(size)}
    <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 100 100" style="position:absolute;top:${-size * 0.16}px;right:${-size * 0.06}px">
      <path d="M50 0 C53.5 27.5 72.5 46.5 100 50 C72.5 53.5 53.5 72.5 50 100 C46.5 72.5 27.5 53.5 0 50 C27.5 46.5 46.5 27.5 50 0 Z" fill="#2E8FD8"/>
    </svg>
  </span>`;
}
export function AioLabel(children, size = 11, style = "") {
  return `<span style="display:inline-flex;align-items:center;gap:4px;${style}">${AioSparkle(size)}<span>${children}</span></span>`;
}

/* ---------- primitives ---------- */
export const Card = (children, { style = "", ai = false, attrs = "" } = {}) =>
  `<div ${attrs} style="background:${ai ? T.magentaSoft : T.surface};border:1px solid ${ai ? `${T.magenta}22` : T.line};border-radius:16px;padding:20px;${style}">${children}</div>`;
export const Lbl = (children, style = "") => `<div style="font-size:10.5px;color:${T.muted};font-weight:600;letter-spacing:0.6px;text-transform:uppercase;margin-bottom:3px;${style}">${children}</div>`;
export const Metric = (children, { color, size, style = "" } = {}) => `<div style="font-size:${size || 22}px;font-weight:800;font-family:${MONO};color:${color || T.ink};${style}">${children}</div>`;
export const Pill = (c, bg, children, style = "") => `<span style="font-size:11px;font-weight:700;color:${c};background:${bg};padding:3px 9px;border-radius:7px;display:inline-block;white-space:nowrap;${style}">${children}</span>`;
export const SectionTitle = (children, { right, style = "" } = {}) =>
  `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;${style}">
    <div style="font-size:14.5px;font-weight:700;color:${T.ink}">${children}</div>
    ${right ? `<div style="font-size:12px;color:${T.muted};font-family:${MONO}">${right}</div>` : ""}
  </div>`;
export const TH = (children, style = "") => `<th style="padding:10px 14px;font-weight:600;font-size:10.5px;color:${T.muted};text-transform:uppercase;letter-spacing:0.5px;text-align:left;border-bottom:1px solid ${T.line};white-space:nowrap;${style}">${children}</th>`;
export const TD = (children, { mono = false, style = "" } = {}) => `<td style="padding:10px 14px;font-size:13px;font-family:${mono ? MONO : FONT};font-weight:${mono ? 600 : 400};border-bottom:1px solid ${T.lineSoft};color:${T.ink};${style}">${children}</td>`;
export const BtnPrimary = (children, { disabled = false, style = "", attrs = "" } = {}) =>
  `<button ${attrs} ${disabled ? "disabled" : ""} style="background:${disabled ? T.lineSoft : T.violet};color:${disabled ? T.muted : "#fff"};border:none;border-radius:11px;font-family:${FONT};font-weight:700;padding:11px 20px;font-size:13.5px;cursor:${disabled ? "default" : "pointer"};display:inline-flex;align-items:center;gap:7px;${style}">${children}</button>`;
export const BtnNeutral = (children, { small = false, style = "", attrs = "" } = {}) =>
  `<button ${attrs} style="background:${T.surface};color:${T.ink};border:1px solid ${T.line};border-radius:11px;font-family:${FONT};font-weight:600;padding:${small ? "6px 12px" : "10px 16px"};font-size:${small ? 12 : 13}px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;${style}">${children}</button>`;
/* Pager — bottom bar for the long list views. Renders nothing when one page suffices.
   wirePager(root, cb) wires the buttons; cb receives the new zero-based page index. */
export function Pager({ page, total, pageSize, noun = "rows" }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (pageCount <= 1) return "";
  const from = page * pageSize + 1, to = Math.min(total, (page + 1) * pageSize);
  const btn = (dir, label, disabled) =>
    `<button data-pager="${dir}" ${disabled ? "disabled" : ""} style="background:${T.surface};color:${disabled ? T.muted : T.ink};border:1px solid ${T.line};border-radius:9px;font-family:${FONT};font-weight:600;padding:5px 12px;font-size:12px;cursor:${disabled ? "default" : "pointer"};opacity:${disabled ? 0.55 : 1}">${label}</button>`;
  return `<div data-pager-bar style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 16px;border-top:1px solid ${T.lineSoft}">
    <div style="font-size:11.5px;color:${T.muted};font-family:${MONO}">${fmt(from)}–${fmt(to)} of ${fmt(total)} ${noun}</div>
    <div style="display:flex;align-items:center;gap:8px">
      ${btn("prev", "‹ Prev", page <= 0)}
      <span style="font-size:11.5px;color:${T.sub};font-family:${MONO}">page ${page + 1} / ${pageCount}</span>
      ${btn("next", "Next ›", page >= pageCount - 1)}
    </div>
  </div>`;
}
export function wirePager(root, page, onPage) {
  root.querySelectorAll("[data-pager]").forEach(b => b.addEventListener("click", () => {
    if (b.disabled) return;
    onPage(b.dataset.pager === "next" ? page + 1 : page - 1);
  }));
}

export const SearchBar = ({ value = "", placeholder, id }) =>
  `<div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:${T.surface};border:1px solid ${T.line};border-radius:11px;flex:1;min-width:200px">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="${T.muted}" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
    <input id="${id}" value="${escapeHtml(value)}" placeholder="${placeholder || "Search…"}" style="border:none;outline:none;font-family:${FONT};font-size:13px;color:${T.ink};background:transparent;flex:1"/>
    ${value ? `<button id="${id}-clear" style="border:none;background:transparent;cursor:pointer;color:${T.muted};font-size:15px;line-height:1;padding:0">×</button>` : ""}
  </div>`;
export const ChipRow = (options, value, group) =>
  `<div style="display:inline-flex;gap:6px;flex-wrap:wrap">
    ${options.map(o => `<button data-chip-group="${group}" data-chip-value="${o}" style="border:1.5px solid ${value === o ? T.violet : T.line};background:${value === o ? T.violetSoft : T.surface};color:${value === o ? T.violet : T.sub};border-radius:999px;padding:5px 13px;font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer">${o}</button>`).join("")}
  </div>`;
export const Stars = score => {
  const filled = Math.max(0, Math.min(5, Math.round(score / 20)));
  return `<span style="color:${T.violet};font-size:13px;letter-spacing:1.5px;white-space:nowrap">${"★".repeat(filled)}<span style="color:${T.line}">${"★".repeat(5 - filled)}</span></span>`;
};
export const ScoreBar = (label, value, color, right) =>
  `<div style="margin-bottom:14px">
    <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px">
      <span style="font-size:12.5px;font-weight:700;color:${T.ink}">${label}</span>
      <span style="font-family:${MONO};font-size:13px;font-weight:800;color:${color}">${right != null ? right : `${value}/100`}</span>
    </div>
    <div style="height:9px;background:${T.lineSoft};border-radius:6px;overflow:hidden">
      <div style="width:${Math.max(2, Math.min(100, value))}%;height:100%;background:${color};border-radius:6px;transition:width .5s ease"></div>
    </div>
  </div>`;
export const Check = c =>
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${c || T.green}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>`;
export const AiTag = children => Pill(T.magenta, T.magentaSoft, children);
export const StatusPill = status => {
  const m = { "AI Winner": [T.magenta, T.magentaSoft], "AI Opportunity": [T.amber, T.amberSoft], "Healthy": [T.green, T.greenSoft], "Low visibility": [T.muted, T.lineSoft] };
  const [c, bg] = m[status] || [T.muted, T.lineSoft];
  return Pill(c, bg, status);
};

/* Inferred AI topic pill with its toggleable explainer popover. */
export const INFERRED_TIP = "Google does not provide the exact AI query. We use your existing Search Console query and page relationships to estimate the most likely topic.";
export function InferredPill() {
  return `<span class="inferred-pill" style="position:relative;display:inline-block">
    <button class="inferred-toggle" title="${INFERRED_TIP}" style="border:1px solid ${T.magenta}33;background:${T.magentaSoft};color:${T.magenta};border-radius:7px;padding:3px 9px;font-family:${FONT};font-size:10.5px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:4px">
      Inferred AI topic
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
    </button>
    <span class="inferred-pop" style="display:none;position:absolute;top:calc(100% + 6px);left:0;z-index:40;width:270px;background:${T.surface};border:1px solid ${T.line};border-radius:12px;padding:12px 14px;font-size:12px;color:${T.sub};line-height:1.5;box-shadow:0 12px 32px rgba(0,0,0,.10);font-weight:500;text-align:left">${INFERRED_TIP}</span>
  </span>`;
}
export function wireInferredPills(root) {
  const pills = [...root.querySelectorAll(".inferred-pill")];
  pills.forEach(pill => {
    pill.querySelector(".inferred-toggle").addEventListener("click", e => {
      e.stopPropagation();
      const pop = pill.querySelector(".inferred-pop");
      const wasClosed = pop.style.display === "none";
      /* Only one explainer at a time — two pills can share a header now. */
      pills.forEach(p => { const pp = p.querySelector(".inferred-pop"); if (pp) pp.style.display = "none"; });
      pop.style.display = wasClosed ? "block" : "none";
    });
  });
}

/* "Open this page in a new tab" — shown next to every URL in the report. A real anchor,
   so right-click / copy-link work; clicks are kept from opening the card behind it. */
export const OpenLink = (url, size = 12) => (url
  ? `<a class="open-url" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" title="Open ${escapeHtml(url)} in a new tab"
       style="display:inline-flex;align-items:center;justify-content:center;color:${T.muted};margin-left:6px;flex-shrink:0;text-decoration:none;vertical-align:middle">
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
    </a>`
  : "");

export function wireOpenLinks(root) {
  root.querySelectorAll("a.open-url").forEach(a => a.addEventListener("click", e => e.stopPropagation()));
}

/* Shared escape helper for interpolated strings inside HTML/attributes. */
export function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
}
